"use strict";

import { CONSTANTS } from "~/helpers/constants";

import { addDomEvent, getElement } from "~/utils/dom";
import { injectCookiedockDOM } from "~/ui/inject/dom";
import { activateScriptsForCategory } from "~/block/script";
import { activateEventListeners } from "~/block/event";
import { injectCookiedockCSS } from "~/ui/inject/style";
import { saveConsentState, storeRejectedCookies } from "./cookie";
import { destroyBanner, logCookiedockError } from "~/helpers";
import { api } from "~/services/api";
import { cuuid } from "..";

import type { Approved, Cookiedock, Rejected } from "~/types";

interface ConsentData {
  accepted: Approved;
  rejected: Rejected[];
  consentChoices: Record<string, boolean>;
}

const ACCEPT_DECLINE = {
  ACCEPT_ALL: "ACCEPTED_ALL",
  ACCEPT_NONE: "ACCEPTED_NONE",
  DECLINED_ALL: "DECLINED_ALL",
  DECLINED_NONE: "DECLINED_NONE",
};

/**
 * Process checkbox data to extract consent information
 */
function processConsentChoices(checkboxes: HTMLInputElement[], cookiePurposes: Cookiedock["cookie_purposes"]): ConsentData {
  const accepted: Approved = {};
  const rejected: Rejected[] = [];
  const consentChoices: Record<string, boolean> = {};

  checkboxes.forEach((checkbox: HTMLInputElement, index: number) => {
    const categoryName = checkbox.name;
    const isApproved = checkbox.checked;

    accepted[categoryName] = {
      id: checkbox.dataset.id ? parseInt(checkbox.dataset.id, 10) : undefined,
      approved: isApproved,
    };

    consentChoices[categoryName] = isApproved;
    consentChoices[CONSTANTS.CATEGORIES.UNKNOWN] = true;

    if (!isApproved) {
      const detectedCookies = cookiePurposes[index]?.detected_cookies || [];
      rejected.push({
        purpose: categoryName,
        cookies: detectedCookies.map(({ cookie_name }) => cookie_name),
      });
    }
  });

  return { accepted, rejected, consentChoices };
}

/**
 * Activate approved categories and handle mock mode
 */
function activateApprovedCategories(consentChoices: Record<string, boolean>, isMockMode: boolean): void {
  Object.entries(consentChoices).forEach(([category, isApproved]) => {
    if (isApproved && !isMockMode) {
      activateCategory(category);
    }
  });
}

/**
 * Update global consent state
 */
function updateGlobalConsentState(consentChoices: Record<string, boolean>): void {
  if (!window.cookiedock.__consent) {
    window.cookiedock.__consent = {
      unknown: true,
    };
  }

  Object.entries(consentChoices).forEach(([category, isApproved]) => {
    window.cookiedock.__consent[category] = isApproved;
  });
}

/**
 * Handle consent submission to API
 */
async function submitConsentToAPI(accepted: Approved): Promise<void> {
  try {
    await api.post(`api/v1/${cuuid}/consent`, { body: { cookie_purposes: accepted } });
  } catch (error) {
    logCookiedockError("Failed to submit consent to API: " + error);
  }
}

/**
 * Process user consent and apply appropriate actions
 */
async function handleConsentSubmission(consentData: ConsentData, isMockMode: boolean): Promise<void> {
  const { accepted, rejected, consentChoices } = consentData;

  if (!isMockMode) {
    activateCategory(CONSTANTS.CATEGORIES.UNKNOWN);
    storeRejectedCookies(rejected);
    saveConsentState(consentChoices);
    updateGlobalConsentState(consentChoices);

    await submitConsentToAPI(accepted);
  }

  activateApprovedCategories(consentChoices, isMockMode);
}

/**
 * Handle consent button click with error handling and banner destruction
 */
async function handleConsentButtonClick(cookie_purposes: Cookiedock["cookie_purposes"], type?: (typeof ACCEPT_DECLINE)[keyof typeof ACCEPT_DECLINE]): Promise<void> {
  try {
    const checkboxes = getElement<HTMLInputElement>(`.${CONSTANTS.PREFERENCES.BANNER}`, document, true);

    if (!checkboxes || checkboxes.length === 0) {
      logCookiedockError("No consent checkboxes found");
      destroyBanner();
      return;
    }

    if (type === ACCEPT_DECLINE.ACCEPT_ALL) {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = true;
      });
    }

    if (type === ACCEPT_DECLINE.DECLINED_ALL) {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }

    const consentData = processConsentChoices(checkboxes, cookie_purposes);
    await handleConsentSubmission(consentData, window.cookiedock.__mock);

    destroyBanner();
  } catch (error) {
    console.error("Error processing consent:", error);
    destroyBanner();
  }
}

function activateDefaultCookiePurposes(cookie_purposes: Cookiedock["cookie_purposes"]): void {
  const checkboxes = getElement<HTMLInputElement>(`.${CONSTANTS.PREFERENCES.BANNER}`, document, true);
  const inputsDialog = getElement<HTMLInputElement>(`.${CONSTANTS.PREFERENCES.DIALOG} input[type="checkbox"]`, document, true);

  cookie_purposes.forEach((purpose) => {
    inputsDialog.forEach((inputDialog) => {
      const label = inputDialog.closest("label") as HTMLLabelElement | null;
      const group = label?.dataset.group;
      if (group === purpose.category && !inputDialog.disabled) {
        inputDialog.checked = purpose.default;
      }
    });

    checkboxes.forEach((checkbox) => {
      if (checkbox.name === purpose.category) {
        checkbox.checked = purpose.default;
      }
    });
  });
}

/**
 * Add event listeners to the consent banner
 */
export async function addListeners({ content, cookie_purposes }: Cookiedock): Promise<void> {
  const existingBanner = getElement<HTMLDivElement>(`.${CONSTANTS.BANNER.SELECTOR}`, document, true);
  if (existingBanner && existingBanner.length > 0) return;

  injectCookiedockDOM(content, cookie_purposes);
  injectCookiedockCSS(content);

  activateDefaultCookiePurposes(cookie_purposes);

  addDomEvent(`.${CONSTANTS.BANNER.CONFIRM}`, "click", () => handleConsentButtonClick(cookie_purposes), true);
  addDomEvent(`.${CONSTANTS.BANNER.ACCEPT}`, "click", () => handleConsentButtonClick(cookie_purposes, ACCEPT_DECLINE.ACCEPT_ALL));
  addDomEvent(`.${CONSTANTS.BANNER.DECLINE}`, "click", () => handleConsentButtonClick(cookie_purposes, ACCEPT_DECLINE.DECLINED_ALL));
}

/**
 * Activate all functionality for a given consent category
 */
export function activateCategory(category: string): void {
  activateScriptsForCategory(category);
  activateEventListeners(category);
}
